import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_VALIDATION_MESSAGES,
  type AttachmentKind,
  deleteAttachment,
  uploadAttachment,
  validateAttachment,
} from '@/api/attachments'
import { AuthError } from '@/api/client'
import { isBusy, isReady, type LocalAttachment } from './types'

/**
 * Chat composer attachment state. Owns:
 *  - the local list of pending/uploading/ready/error attachments
 *  - the per-file upload lifecycle (XHR-like progress via `signal`)
 *  - object-URL lifecycle for inline thumbnails (created in `addFiles`,
 *    revoked when the attachment is removed or the hook unmounts)
 *
 * The hook is intentionally decoupled from React Query — chat attachments
 * are session-local; we don't want them re-fetched on focus or cached in
 * a key. The list resets when the chat is cleared.
 */
export interface ChatAttachmentsApi {
  /** Read-only snapshot for the UI. */
  attachments: readonly LocalAttachment[]
  /** Any client-side validation error from the most recent `addFiles` call. */
  lastValidationError: string | null
  /** True iff at least one attachment is still uploading. */
  uploading: boolean
  /** All ready attachments — convenience for the send handler. */
  readyAttachments: readonly LocalAttachment[]
  /**
   * Add one or more files (e.g. from a drag/drop, picker, or paste).
   * Returns the localIds of the successfully *queued* attachments; files
   * that fail client-side validation are skipped (and recorded in
   * `lastValidationError`).
   */
  addFiles: (
    files: readonly File[],
    options?: { source?: 'paste' | 'screenshot' | 'file' },
  ) => string[]
  /** Cancel an in-flight upload or remove a finished/failed one. */
  remove: (localId: string) => Promise<void>
  /** Retry an attachment that errored mid-upload (transient failure). */
  retry: (localId: string) => void
  /** Drop the whole list — used when the chat is cleared / unmounted. */
  reset: () => void
  /** Drop the dismissible validation banner. */
  clearValidationError: () => void
}

/**
 * Heuristic kind for pre-upload chip styling. The server reports its own
 * `kind` once ingestion is done, but we still need *something* before the
 * round-trip resolves.
 */
function guessKind(file: File, isScreenshot: boolean): AttachmentKind {
  if (isScreenshot) return 'screenshot'
  const mime = file.type.toLowerCase()
  if (mime.startsWith('image/')) return 'image'
  if (mime === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    return 'pdf'
  }
  if (mime.startsWith('text/')) return 'text'
  if (mime === 'application/json') return 'text'
  return 'other'
}

function makeObjectUrl(file: File): string | null {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null
  }
  if (!file.type.startsWith('image/')) return null
  try {
    return URL.createObjectURL(file)
  } catch {
    return null
  }
}

function revoke(url: string | null) {
  if (!url) return
  try {
    URL.revokeObjectURL(url)
  } catch {
    // jsdom / older browsers — ignore.
  }
}

let idCounter = 0
function nextId(): string {
  idCounter += 1
  return `att-${Date.now().toString(36)}-${idCounter}`
}

interface PendingFileEntry {
  localId: string
  file: File
  source: 'paste' | 'screenshot' | 'file' | undefined
}

export function useChatAttachments(): ChatAttachmentsApi {
  const [attachments, setAttachments] = useState<LocalAttachment[]>([])
  const [lastValidationError, setLastValidationError] = useState<string | null>(
    null,
  )
  // Track the File objects per pending/uploading entry so retry can re-send
  // without forcing the user to re-pick the file from disk.
  const filesRef = useRef(new Map<string, PendingFileEntry>())
  const abortersRef = useRef(new Map<string, AbortController>())
  // Generation counter — bumped each time we restart an upload for a given
  // local id. Stale handlers (resolved after a retry started) check this
  // counter and bail so they don't overwrite the newer attempt's state.
  const generationsRef = useRef(new Map<string, number>())
  // Live set of `URL.createObjectURL` strings the hook owns. We add on
  // creation and delete on revocation; the unmount cleanup walks whatever
  // remains so blob URLs don't leak when the composer goes away with
  // attachments still on the rail.
  const previewUrlsRef = useRef(new Set<string>())

  // Tear down on unmount. Aborts in-flight uploads and revokes any object
  // URLs still alive — handles the "user navigated away mid-upload" and
  // "composer unmounted with chips on the rail" cases.
  useEffect(() => {
    // Capture the structures in local vars so the cleanup function isn't
    // reaching through `*Ref.current` at unmount time (lint flags that
    // pattern even though our refs are stable for the hook's lifetime).
    const files = filesRef.current
    const aborters = abortersRef.current
    const previewUrls = previewUrlsRef.current
    return () => {
      files.clear()
      aborters.forEach((ctl) => ctl.abort())
      aborters.clear()
      previewUrls.forEach((url) => revoke(url))
      previewUrls.clear()
    }
  }, [])

  const startUpload = useCallback((localId: string) => {
    const entry = filesRef.current.get(localId)
    if (!entry) return
    const generation = (generationsRef.current.get(localId) ?? 0) + 1
    generationsRef.current.set(localId, generation)

    const controller = new AbortController()
    abortersRef.current.set(localId, controller)

    setAttachments((prev) =>
      prev.map((a) =>
        a.localId === localId
          ? {
              ...a,
              status: 'uploading',
              abort: () => controller.abort(),
            }
          : a,
      ),
    )

    ;(async () => {
      try {
        const server = await uploadAttachment({
          file: entry.file,
          source: entry.source,
          signal: controller.signal,
        })
        if (generationsRef.current.get(localId) !== generation) return
        setAttachments((prev) =>
          prev.map((a) =>
            a.localId === localId
              ? {
                  ...a,
                  status: 'ready',
                  server,
                }
              : a,
          ),
        )
      } catch (err) {
        if (generationsRef.current.get(localId) !== generation) return
        if (
          err instanceof DOMException &&
          err.name === 'AbortError'
        ) {
          // Aborted by the user — no state mutation needed; the `remove`
          // caller is the one that flipped status and already pulled us
          // out of `attachments`.
          return
        }
        const isAuth = err instanceof AuthError
        const message =
          err instanceof Error
            ? err.message
            : 'Upload failed — try again or pick a different file.'
        setAttachments((prev) =>
          prev.map((a) =>
            a.localId === localId
              ? {
                  ...a,
                  status: 'error',
                  message,
                  permanent: isAuth,
                }
              : a,
          ),
        )
      } finally {
        abortersRef.current.delete(localId)
      }
    })()
  }, [])

  const addFiles = useCallback<ChatAttachmentsApi['addFiles']>(
    (files, options) => {
      if (files.length === 0) return []
      const source = options?.source ?? 'file'
      const isScreenshot = source === 'paste' || source === 'screenshot'

      const queued: string[] = []
      const newAttachments: LocalAttachment[] = []
      let validationError: string | null = null

      for (const file of files) {
        const err = validateAttachment(file)
        if (err) {
          // Show the first error to the user, but keep going through the
          // rest of the batch so partial drops still upload the valid
          // files. The chat composer also surfaces a toast.
          if (!validationError) validationError = err
          continue
        }
        // Server caps context_files at ATTACHMENT_MAX_COUNT per fill (413
        // otherwise). Mirror the cap client-side so the user sees the
        // refusal before they hit send. ``filesRef`` grows inside this
        // loop as each accepted file is queued, so checking its size on
        // each iteration counts both pre-existing chips and the ones
        // accepted earlier in this same batch — no separate counter needed.
        if (filesRef.current.size >= ATTACHMENT_MAX_COUNT) {
          if (!validationError) {
            validationError = ATTACHMENT_VALIDATION_MESSAGES.TOO_MANY
          }
          continue
        }
        const localId = nextId()
        const localPreviewUrl = makeObjectUrl(file)
        if (localPreviewUrl) previewUrlsRef.current.add(localPreviewUrl)
        const next: LocalAttachment = {
          localId,
          filename: file.name || (isScreenshot ? 'Screenshot.png' : 'attachment'),
          byteSize: file.size,
          mimeType: file.type || guessMimeFromName(file.name),
          kind: guessKind(file, isScreenshot),
          isScreenshot,
          localPreviewUrl,
          status: 'pending',
        }
        filesRef.current.set(localId, { localId, file, source })
        newAttachments.push(next)
        queued.push(localId)
      }

      if (validationError) setLastValidationError(validationError)
      else setLastValidationError(null)

      if (newAttachments.length === 0) return queued

      setAttachments((prev) => [...prev, ...newAttachments])
      // Kick off uploads after state has been queued — we don't want
      // startUpload's setState to clobber the pending entry we just added.
      // React batches both updates inside a single tick, so calling this
      // synchronously is fine.
      newAttachments.forEach((a) => startUpload(a.localId))
      return queued
    },
    [startUpload],
  )

  const remove = useCallback<ChatAttachmentsApi['remove']>(
    async (localId) => {
      const target = attachments.find((a) => a.localId === localId)
      const ctl = abortersRef.current.get(localId)
      if (ctl) ctl.abort()
      abortersRef.current.delete(localId)
      filesRef.current.delete(localId)
      generationsRef.current.delete(localId)
      setAttachments((prev) => {
        const next = prev.filter((a) => a.localId !== localId)
        return next
      })
      if (target?.localPreviewUrl) {
        previewUrlsRef.current.delete(target.localPreviewUrl)
        revoke(target.localPreviewUrl)
      }
      if (target && isReady(target)) {
        try {
          await deleteAttachment(target.server.uuid)
        } catch {
          // Server-side delete is best-effort: the chip is already gone
          // from the UI. The backend's R2 janitor sweeps orphaned uploads
          // after 24h, so a lost DELETE doesn't leak forever.
        }
      }
    },
    [attachments],
  )

  const retry = useCallback<ChatAttachmentsApi['retry']>(
    (localId) => {
      const target = attachments.find((a) => a.localId === localId)
      if (!target || target.status !== 'error' || target.permanent) return
      setAttachments((prev) =>
        prev.map((a) =>
          a.localId === localId
            ? {
                ...a,
                status: 'pending',
              }
            : a,
        ),
      )
      startUpload(localId)
    },
    [attachments, startUpload],
  )

  const reset = useCallback<ChatAttachmentsApi['reset']>(() => {
    abortersRef.current.forEach((ctl) => ctl.abort())
    abortersRef.current.clear()
    attachments.forEach((a) => {
      if (a.localPreviewUrl) {
        previewUrlsRef.current.delete(a.localPreviewUrl)
        revoke(a.localPreviewUrl)
      }
    })
    filesRef.current.clear()
    generationsRef.current.clear()
    setAttachments([])
    setLastValidationError(null)
  }, [attachments])

  const clearValidationError = useCallback(() => {
    setLastValidationError(null)
  }, [])

  const uploading = attachments.some(isBusy)
  const readyAttachments = attachments.filter(isReady)

  return {
    attachments,
    lastValidationError,
    uploading,
    readyAttachments,
    addFiles,
    remove,
    retry,
    reset,
    clearValidationError,
  }
}

function guessMimeFromName(name: string): string {
  const lower = name.toLowerCase()
  if (lower.endsWith('.png')) return 'image/png'
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg'
  if (lower.endsWith('.gif')) return 'image/gif'
  if (lower.endsWith('.webp')) return 'image/webp'
  if (lower.endsWith('.pdf')) return 'application/pdf'
  if (lower.endsWith('.txt') || lower.endsWith('.log')) return 'text/plain'
  if (lower.endsWith('.md')) return 'text/markdown'
  if (lower.endsWith('.json')) return 'application/json'
  if (lower.endsWith('.csv')) return 'text/csv'
  return 'application/octet-stream'
}
